<template>
  <div class="zip-code" ref="zipCode">
    <div class="zip-code__label">{{ label }}</div>
    <div class="zip-code__value">
      <input type="text" maxlength="2" ref="zipFirstInput"
        data-vv-validate-on="change" name="zipCodeFirstPart"
        @input="input" @focus="onFocusTextInput" @blur="onBlurTextInput"
        v-model="firstPart" v-validate="{ required: true, numeric: true, min: 2 }">
      <input type="text" maxlength="3" ref="zipSecondInput"
        data-vv-validate-on="change" name="zipCodeSecondPart"
        @input="input" @focus="onFocusTextInput" @blur="onBlurTextInput"
        v-model="secondPart" v-validate="{ required: true, numeric: true, min: 3 }">
    </div>
    <span class="zip-code__divider" />

    <div v-if="validationErrors" class="v-field-error">{{ validationError }}</div>
  </div>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'

import CustomControlValidationMixin from '@/components/CustomControlValidationMixin'
import TextInput from '@/components/forms/fields/TextInput.vue'
import Observer from '@/utils/observer'

@Component({
  mixins: [
    CustomControlValidationMixin,
  ],
  components: {
    TextInput,
  },
  computed: {
    zipCode () {
      return `${this.firstPart}-${this.secondPart}`
    },
  },
  props: {
    label: {
      type: String,
      required: false,
    },
    name: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        if (newValue) {
          const [ first, second ] = newValue.split('-')
          this.firstPart = first || ''
          this.secondPart = second || ''
          
          this.$nextTick(() => {
            if (this.$validator) {
              this.$validator.validate('zipCodeFirstPart', this.firstPart)
              this.$validator.validate('zipCodeSecondPart', this.secondPart)
            }
            this.updateComponentClass()
          })
        }
      },
    },
  },
})
export default class ZipCode extends Vue {
  firstPart = ''
  secondPart = ''
  firstInput = null
  secondInput = null
  timeout = null
  errorOverride = ''

  mounted () {
    this.firstInput = this.$refs.zipFirstInput
    this.secondInput = this.$refs.zipSecondInput
    if (this.value) {
      this.$nextTick(() => {
        if (this.$validator) {
          this.$validator.validate('zipCodeFirstPart', this.firstPart)
          this.$validator.validate('zipCodeSecondPart', this.secondPart)
        }
        this.updateComponentClass()
      })
    }
  }

  get validationErrors () {
    return this.errors.has('zipCodeFirstPart') || this.errors.has('zipCodeSecondPart') || this.errorOverride.length > 0
  }

  get validationError () {
    return this.errors.first('zipCodeFirstPart') || this.errors.first('zipCodeSecondPart') || this.errorOverride
  }

  fieldsValid () {
    return this.fieldContainClass(this.firstInput, 'field-valid')
      && this.fieldContainClass(this.secondInput, 'field-valid')
  }

  isAnyFieldDirty () {
    return this.fieldContainClass(this.firstInput, 'dirty') 
      || this.fieldContainClass(this.secondInput, 'dirty')
  }

  fieldsInvalid () {
    return this.fieldContainClass(this.firstInput, 'field-invalid')
      || this.fieldContainClass(this.secondInput, 'field-invalid')
  }

  fieldContainClass (field, className) {
    return field.classList.contains(className)
  }

  updateComponentClass () {
    if (this.fieldsValid()) {
      this.$refs.zipCode.classList.add('field-valid')
      this.$refs.zipCode.classList.remove('field-invalid')
    } else if (this.fieldsInvalid()) {
      this.$refs.zipCode.classList.add('field-invalid')
      this.$refs.zipCode.classList.remove('field-valid')
    }
  }

  updateComponentClassStrict () {
    if (this.fieldsValid()) {
      this.$refs.zipCode.classList.add('field-valid')
      this.$refs.zipCode.classList.remove('field-invalid')
    } else if (this.isAnyFieldDirty()) {
      this.$refs.zipCode.classList.add('field-invalid')
      this.$refs.zipCode.classList.remove('field-valid')
      this.errorOverride = 'Wpisz poprawny kod pocztowy w formacie XX-XXX'
    }
  }

  watchForFieldMutation (target) {
    this.errorOverride = ''
    const config = { attributes: true }
    const type = 'attributes'

    return new Observer(target, config, type).observe(this.updateComponentClass) 
  }

  input () {
    this.watchForFieldMutation(this.firstInput)
    this.watchForFieldMutation(this.secondInput)

    if (this.firstPart.length === 2) this.secondInput.focus()
    if (this.secondPart.length === 3 && this.firstPart.length !== 2) this.firstInput.focus()

    if (this.firstPart.length === 2 && this.secondPart.length === 3) {
      this.$emit('input', this.zipCode)
    }
  }

  onFocusTextInput (e) {
    return e.target.closest('.zip-code').classList.add('zip-code--focus')
  }

  onBlurTextInput (e) {
    e.target.closest('.zip-code').classList.remove('zip-code--focus')
    this.$nextTick(() => {
      if (!e.target.closest('.zip-code').classList.contains('zip-code--focus')) {
        this.updateComponentClassStrict()
      }
    })
  }
}
</script>

<style lang="scss">
@import '../FormValidationIndicator.scss';

.zip-code {
  @include form-validation-indicator(21px, 5px);

  &__label {
    font-size: 12px;
  }
  
  &__value {
    font-size: 13px;
    position: relative;
    display: grid;
    grid-template-columns: 28px 1fr;

    input {
      background-color: transparent;
      border: none;
      border-bottom: 1px solid #0057A8;
      border-radius: 0;
      padding: 5px 0 6px;
      width: 100%;
      outline: none;

      @include mediaFluid(tablet) {
        padding: 6px 0 7px;
      }
    }
  }

  &__divider {
    position: absolute;
    width: 4px;
    height: 1px;
    background: rgba(0,92,169, .5);
    top: 29px;
    left: 19px;
  }

  &.field-invalid .zip-code__divider {
    background-color: rgba(231, 31, 108, .5);
  }

  &--focus:not(.field-invalid) .zip-code__value {
    color: #009FEA;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/forms/fields/ZipCode.vue